/**
 * Admin AI model registry hooks using TanStack Query.
 *
 * Mirrors useAdminLoginProviders.ts's query/mutation shape: one list query
 * plus create/update/delete mutations that invalidate it, and a separate
 * status query for the gateway routability banner.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listAiModels,
  createAiModel,
  updateAiModel,
  deleteAiModel,
  getGatewayStatus,
  getAiModelPrice,
  setAiModelPrice,
  clearAiModelPrice,
  type EffectivePrice,
  type PriceOverrideBody,
  type AiModel,
  type AiModelCreate,
  type GatewayStatus,
} from '@services/aiModelsService';

const AI_MODELS_KEY = ['admin-ai-models'];
const AI_PRICE_KEY = ['admin-ai-model-price'];
const AI_GATEWAY_STATUS_KEY = ['admin-ai-gateway-status'];

export function useAiModels() {
  return useQuery<AiModel[]>({
    queryKey: AI_MODELS_KEY,
    queryFn: listAiModels,
  });
}

export function useGatewayStatus() {
  return useQuery<GatewayStatus>({
    queryKey: AI_GATEWAY_STATUS_KEY,
    queryFn: getGatewayStatus,
  });
}

export function useCreateAiModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: Partial<AiModelCreate>) => createAiModel(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AI_MODELS_KEY });
      void queryClient.invalidateQueries({ queryKey: AI_GATEWAY_STATUS_KEY });
    },
  });
}

export function useUpdateAiModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ modelId, body }: { modelId: string; body: Partial<AiModelCreate> }) =>
      updateAiModel(modelId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AI_MODELS_KEY });
      void queryClient.invalidateQueries({ queryKey: AI_GATEWAY_STATUS_KEY });
    },
  });
}

export function useDeleteAiModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (modelId: string) => deleteAiModel(modelId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AI_MODELS_KEY });
      void queryClient.invalidateQueries({ queryKey: AI_GATEWAY_STATUS_KEY });
    },
  });
}

export function useAiModelPrice(modelId: string) {
  return useQuery<EffectivePrice | null>({
    queryKey: [...AI_PRICE_KEY, modelId],
    queryFn: () => getAiModelPrice(modelId),
  });
}

export function useSetAiModelPrice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ modelId, body }: { modelId: string; body: PriceOverrideBody }) =>
      setAiModelPrice(modelId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AI_PRICE_KEY });
    },
  });
}

export function useClearAiModelPrice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (modelId: string) => clearAiModelPrice(modelId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AI_PRICE_KEY });
    },
  });
}
